// Rendu de la vue du viseur sur canvas 2D (résolution interne = taille CSS × dpr).
// La fenêtre du viseur couvre toujours VIEW_SIZE unités monde : le canvas applique l'échelle.
// Le décor du niveau (ville, plage, forêt, montagnes, jungle, cimetière — voir ui/sceneryPainter.js)
// est peint derrière : ciel au-dessus de la ligne d'horizon (y = 0 du monde), sol en dessous, et
// deux plans de silhouettes en parallaxe.
// Les fantômes sont dessinés avec leur vrai portrait (voir data/family.js), avec un effet de
// perspective : petit près du haut du décor (ciel), grand près du bas (sol) — une propriété de la
// position verticale absolue du fantôme sur la carte, jamais de la caméra ni de sa position
// courante à l'écran (voir drawGhosts). Un halo doux de la couleur du fantôme est dessiné derrière.
// Un kami déjà dans l'album (known, #20) est estompé, sans halo, avec une coche verte : il ne se photographie plus.
// Seuls les éléments visibles dans la fenêtre sont dessinés (culling).
import { VIEW_SIZE, GHOST_MIN_SIZE, GHOST_MAX_SIZE, KNOWN_GHOST_ALPHA } from '../config/constants.js';
import { view, viewWidthFor } from './viewState.js';
import { worldToView, isInView } from '../core/camera.js';
import { HORIZON_Y } from '../core/scenery.js';
import { sceneryPalettes } from '../config/theme.js';
import { createSceneryPainter } from './sceneryPainter.js';
import { familySprite, FAMILY_MEMBERS } from '../data/family.js';

/**
 * L'allure d'un kami dans le viseur : un kami à photographier, opaque avec son halo ; un kami déjà dans l'album (#20), estompé, sans
 * halo, avec la coche de l'album.
 * @param {{ known?: boolean }} ghost
 * @returns {{ alpha: number, halo: boolean, albumMark: boolean }}
 */
export function ghostLook(ghost) {
  return ghost.known
    ? { alpha: KNOWN_GHOST_ALPHA, halo: false, albumMark: true }
    : { alpha: 1, halo: true, albumMark: false };
}

export function createRenderer(canvas, container, theme) {
  const ctx = canvas.getContext('2d');
  const scenery = createSceneryPainter(ctx, canvas);
  const spriteCache = new Map(); // src -> HTMLImageElement (chargement asynchrone, mémorisé)

  /** Image d'un portrait de fantôme, chargée une seule fois et réutilisée (mémorisée par src). */
  function loadSprite(src) {
    let img = spriteCache.get(src);
    if (!img) {
      img = new Image();
      img.src = src;
      spriteCache.set(src, img);
    }
    return img;
  }
  // Lance le chargement des 15 portraits dès la création du renderer (écran d'accueil / saisie du
  // profil), plutôt que d'attendre que chaque kami entre dans le cadre pour la première fois : sur
  // une connexion lente, ça laissait le temps de voir un halo flou sans portrait dessus (voir
  // drawGhosts, qui n'affiche justement plus rien tant que le portrait n'est pas prêt).
  for (const member of FAMILY_MEMBERS) loadSprite(member.sprite);

  /** Ajuste la résolution du canvas à sa taille CSS. Retourne false si le conteneur n'est pas mesurable. */
  function resize() {
    const cssWidth = container.clientWidth;
    const cssHeight = container.clientHeight;
    if (cssWidth <= 0 || cssHeight <= 0) return false;
    const dpr = window.devicePixelRatio || 1;
    const pxWidth = Math.round(cssWidth * dpr);
    const pxHeight = Math.round(cssHeight * dpr);
    if (canvas.width !== pxWidth || canvas.height !== pxHeight) {
      canvas.width = pxWidth;
      canvas.height = pxHeight;
    }
    // Carré en portrait ; en paysage le cadre prend tout l'écran : la hauteur reste VIEW_SIZE unités, la largeur s'étend
    view.width = viewWidthFor(pxWidth, pxHeight);
    return true;
  }

  function drawGhosts(ghosts, camera, scale, worldSize) {
    const margin = GHOST_MAX_SIZE / 2;
    for (const g of ghosts) {
      if (g.captured) continue; // photographié : disparaît du viseur, ne reste que dans la galerie
      if (!isInView(g, camera, margin, VIEW_SIZE, worldSize, view.width)) continue;
      // Portrait pas encore chargé (voir le préchargement plus haut, ou une connexion lente) :
      // on ne dessine rien plutôt qu'un halo tout seul, qui se voit comme une tache floue sans
      // personnage dessus — mieux vaut que le kami apparaisse un peu en retard que flou.
      const sprite = loadSprite(familySprite(g.familyIndex));
      if (!(sprite.complete && sprite.naturalWidth > 0)) continue;

      const v = worldToView(g, camera, VIEW_SIZE, worldSize, view.width);
      // Perspective : petit près du haut du décor (ciel, loin), grand près du bas (sol, proche).
      // Propriété de la position VERTICALE ABSOLUE du kami sur la carte (g.y ; l'horizon est à 0,
      // le ciel en négatif, le sol en positif) — jamais de la caméra ni de la position à l'écran :
      // un kami immobile garde la même taille qu'on bouge la caméra ou pas, et un kami qui descend
      // grandit même caméra fixe. La référence est VIEW_SIZE (une "hauteur d'écran" de part et
      // d'autre de l'horizon), pas worldSize (qui grandit avec le niveau et rendrait l'effet
      // imperceptible : deux kami visibles ensemble ne différeraient jamais que de la hauteur du
      // viseur sur une échelle bien plus grande).
      const t = Math.min(1, Math.max(0, (g.y + VIEW_SIZE / 2) / VIEW_SIZE));
      const size = (GHOST_MIN_SIZE + (GHOST_MAX_SIZE - GHOST_MIN_SIZE) * t) * scale;
      const px = v.x * scale;
      const py = v.y * scale;

      const look = ghostLook(g);
      // Halo doux derrière le portrait, coloré selon le fantôme
      if (look.halo) {
        const haloR = size * 0.55;
        const halo = ctx.createRadialGradient(px, py, haloR * 0.15, px, py, haloR);
        halo.addColorStop(0, `${g.color}55`);
        halo.addColorStop(1, `${g.color}00`);
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(px, py, haloR, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = look.alpha;
      ctx.drawImage(sprite, px - size / 2, py - size / 2, size, size);
      ctx.globalAlpha = 1;
      if (look.albumMark) drawAlbumMark(px + size * 0.32, py - size * 0.32, Math.max(7 * scale, size * 0.13));
    }
    ctx.globalAlpha = 1;
  }

  /** Coche « déjà dans l'album » : une pastille verte (couleur de la capture) cerclée de sombre, une coche blanche. */
  function drawAlbumMark(cx, cy, r) {
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = theme?.toxicGreen ?? '#22c55e';
    ctx.fill();
    ctx.lineWidth = Math.max(1, r * 0.22);
    ctx.strokeStyle = 'rgba(10, 6, 20, 0.85)';
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - r * 0.45, cy + r * 0.02);
    ctx.lineTo(cx - r * 0.12, cy + r * 0.36);
    ctx.lineTo(cx + r * 0.48, cy - r * 0.32);
    ctx.lineWidth = Math.max(1.5, r * 0.28);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
  }

  /**
   * @param {object} s état de rendu
   * @param {import('../core/world.js').Ghost[]} s.ghosts
   * @param {{x:number,y:number}} s.camera
   * @param {number} s.worldSize
   * @param {import('../core/scenery.js').Scenery} [s.scenery]
   */
  function draw(s) {
    if (!s.ghosts || canvas.width === 0) return;
    const scale = canvas.height / VIEW_SIZE; // px par unité monde (la hauteur du cadre vaut VIEW_SIZE unités)
    const horizonPx = worldToView({ x: 0, y: HORIZON_Y }, s.camera).y * scale;

    const palette = sceneryPalettes[s.scenery?.theme] ?? sceneryPalettes.city;
    scenery.paint(s.scenery ?? null, palette, s.camera, horizonPx, scale, performance.now() / 1000);
    // Pas de ligne de bord de monde : l'axe X boucle (voir core/camera.js#wrapX), il n'y a plus de bord.
    drawGhosts(s.ghosts, s.camera, scale, s.worldSize);
  }

  return { resize, draw };
}
